import React, { FC, Fragment, ReactElement, RefObject, useCallback, useMemo } from "react";
import { ThemeProvider, Tooltip } from "@evergis/ui";
import { PieChartData } from "@evergis/charts";

import {
  StackBarAlias,
  StackBarContainer,
  StackBarHeader,
  StackBarSection,
  StackBarTotal,
  StackBarUnits,
  StackBarValue,
} from "./styled";
import { ChartTooltip, ChartTooltipColor, ChartTooltipName, ChartTooltipTable } from "../../styled";
import { formatAttributeValue } from "../../../../utils/formatAttributeValue";
import { useFeatureFilters } from "../../hooks/useFeatureFilters";
import { ConfigContainerChild, ConfigOptions, ContainerProps, FilterItem, WidgetType } from "../../types";
import { ChartDataProps } from "../types";

export const StackBar: FC<{
  data: ChartDataProps[];
  filterName: string;
  type: WidgetType;
  alias: ConfigContainerChild;
  options: ConfigOptions;
  renderElement: ContainerProps["renderElement"];
  renderTooltip: (data: PieChartData[]) => ReactElement | string;
}> = ({ data, filterName, type, alias, options, renderElement, renderTooltip }) => {
  const { height, showTotal, cornerRadius, groupTooltip } = options || {};
  const { hasAnyFilter, isFiltered, onFilter } = useFeatureFilters(type, filterName, data?.[0]?.items as FilterItem[]);

  const { items, layerInfo, attributeName } = data?.[0] || {};
  const attribute = layerInfo?.layerDefinition.attributes[attributeName];
  const units = attribute?.stringFormat?.unitsLabel;

  const total = useMemo(
    () => (items as FilterItem[])?.reduce((result, { value }) => result + Number(value), 0) || 0,
    [items],
  );

  const getWidth = useCallback(value => ((Number(value) / total) * 100).toFixed(2), [total]);

  const renderGroupTooltip = useMemo(
    () => (
      <ThemeProvider>
        <ChartTooltipTable cellPadding={0} cellSpacing={0}>
          {items?.map(({ name, value, color }, index: number) => (
            <tr key={index}>
              <td>
                <ChartTooltip>
                  <ChartTooltipColor $color={color} />
                  <ChartTooltipName>{name}</ChartTooltipName>
                </ChartTooltip>
              </td>
              <td>{value}</td>
            </tr>
          ))}
        </ChartTooltipTable>
      </ThemeProvider>
    ),
    [items],
  );

  const renderItem = useCallback(
    ({ name, value, color }: FilterItem, ref?: RefObject<HTMLDivElement>) => (
      <StackBarSection
        ref={ref}
        $width={getWidth(value)}
        $height={height}
        $color={color}
        cornerRadius={cornerRadius}
        hasAnyFilter={hasAnyFilter}
        isFiltered={isFiltered(name)}
        onClick={filterName ? () => onFilter(name) : undefined}
      />
    ),
    [cornerRadius, filterName, getWidth, hasAnyFilter, height, isFiltered, onFilter],
  );

  const renderItems = useMemo(
    () => (
      <>
        {items?.map((item, index) => (
          <Fragment key={index}>
            {groupTooltip ? (
              renderItem(item)
            ) : (
              <ThemeProvider>
                <Tooltip placement={"top"} arrow content={renderTooltip([item])}>
                  {ref => renderItem(item, ref)}
                </Tooltip>
              </ThemeProvider>
            )}
          </Fragment>
        ))}
      </>
    ),
    [groupTooltip, items, renderItem, renderTooltip],
  );

  if (!total || !attribute) return null;

  return (
    <>
      {(alias || showTotal) && (
        <StackBarHeader>
          <StackBarAlias>{renderElement({ id: "alias" })}</StackBarAlias>
          {showTotal && (
            <StackBarTotal>
              <StackBarValue>{formatAttributeValue(attribute.type, total, attribute.stringFormat, true)}</StackBarValue>
              {!!units && <StackBarUnits>{units}</StackBarUnits>}
            </StackBarTotal>
          )}
        </StackBarHeader>
      )}
      {groupTooltip ? (
        <Tooltip placement={"top"} arrow content={renderGroupTooltip}>
          {ref => <StackBarContainer ref={ref}>{renderItems}</StackBarContainer>}
        </Tooltip>
      ) : (
        <StackBarContainer>{renderItems}</StackBarContainer>
      )}
    </>
  );
};
